import React, { useEffect, useState } from 'react'
import * as RadixDialog from '@radix-ui/react-dialog'
import { tv } from 'tailwind-variants'
import Button from 'components/button'
import { MenuLayer } from 'components/menu'
import { Cross } from 'components/icons'

// In the manner of shadcn/ui's dialog, on Radix's: a dimmed page, and a card in the middle of the window
// with a title, a line saying what it's for, its content, and its buttons at the foot. Radix keeps focus in
// it while it's open and returns it after, stops the page behind from scrolling, closes it on Escape or a
// press outside it, and names it for screen readers by its title and description.
const dialog = tv({
  slots: {
    overlay: 'fixed inset-0 z-[3500] bg-black/40 animate-overlay-in motion-reduce:animate-none',
    // Centred by a flex box rather than a transform, so menus fixed inside it place themselves against the
    // window.
    position: 'fixed inset-0 z-[3500] flex items-center justify-center p-4 pointer-events-none',
    content: [
      'pointer-events-auto relative flex max-h-[min(720px,calc(100vh-32px))] w-full flex-col',
      'rounded-xl border border-divider bg-surface-raised text-[13px] text-body outline-none',
      'shadow-[0_16px_48px_-12px_rgb(0_0_0/0.25),0_4px_12px_-4px_rgb(0_0_0/0.08)]',
      'animate-dialog-in motion-reduce:animate-none'
    ],
    header: 'flex flex-col gap-1 px-5 pt-5',
    // Clear of the close button; the description below runs under it.
    title: 'pr-8 text-[16px] font-semibold leading-6 text-body',
    description: 'text-pretty text-muted',
    body: 'min-h-0 flex-1 overflow-y-auto px-5 py-4',
    // The quieter surface of the menus' and popovers' footers, to the card's rounded corners.
    footer: 'flex items-center justify-end gap-2 rounded-b-xl border-t border-divider bg-surface-muted px-5 py-3.5',
    close: 'absolute top-3.5 right-3.5 size-8 p-0'
  },
  variants: {
    width: {
      small: { content: 'max-w-[420px]' },
      medium: { content: 'max-w-[460px]' },
      large: { content: 'max-w-[680px]' }
    }
  },
  defaultVariants: { width: 'medium' }
})

const CLOSE_ALL_DIALOGS = 'morta:close-all-dialogs'

/**
 * Closes every open dialog but one that must be answered, for something that needs the page a dialog keeps to
 * itself: AI Support, opened from an error over one, couldn't otherwise be used.
 */
export const closeAllDialogs = (): void => {
  window.dispatchEvent(new Event(CLOSE_ALL_DIALOGS))
}

interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** A line under the title saying what the dialog is for. */
  description?: React.ReactNode
  /** Its buttons, at the foot: Cancel, then the action, on the right. */
  footer?: React.ReactNode
  width?: 'small' | 'medium' | 'large'
  /** False for a choice that must be made: no close button, and Escape or a press outside leaves it open. */
  dismissible?: boolean
  children: React.ReactNode
}

/**
 * A modal dialog. Menus, dropdowns and popovers opened from its content render in a layer of its own, so
 * they count as inside it: focus can reach them, and pressing one doesn't close the dialog.
 */
const Dialog: React.FC<DialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  footer,
  width,
  dismissible = true,
  children
}) => {
  const [layer, setLayer] = useState<HTMLElement | null>(null)
  const styles = dialog({ width })

  useEffect(() => {
    if (!open || !dismissible) return
    const close = () => onOpenChange(false)
    window.addEventListener(CLOSE_ALL_DIALOGS, close)
    return () => window.removeEventListener(CLOSE_ALL_DIALOGS, close)
  }, [open, dismissible, onOpenChange])

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={styles.overlay()} />
        <div className={styles.position()}>
          <RadixDialog.Content
            className={styles.content()}
            // Escape closes an open menu first, and the dialog only once none is.
            onEscapeKeyDown={(event) => {
              if (!dismissible || layer?.childElementCount) event.preventDefault()
            }}
            onInteractOutside={(event) => {
              if (!dismissible) event.preventDefault()
            }}
            // Without a description, say so, rather than leaving screen readers a dangling reference.
            {...(description ? {} : { 'aria-describedby': undefined })}
          >
            <MenuLayer.Provider value={layer}>
              <div className={styles.header()}>
                <RadixDialog.Title className={styles.title()}>{title}</RadixDialog.Title>
                {description && (
                  <RadixDialog.Description className={styles.description()}>{description}</RadixDialog.Description>
                )}
              </div>
              <div className={styles.body()}>{children}</div>
              {footer && <div className={styles.footer()}>{footer}</div>}
              {dismissible && (
                <RadixDialog.Close asChild>
                  <Button internalType="transparent" aria-label="Close" className={styles.close()}>
                    <Cross />
                  </Button>
                </RadixDialog.Close>
              )}
            </MenuLayer.Provider>
            {/* Where its menus render: the whole window, inside the dialog; only the menus take presses. */}
            <div ref={setLayer} className="pointer-events-none fixed inset-0 z-[1] [&>*]:pointer-events-auto" />
          </RadixDialog.Content>
        </div>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

export default Dialog
